import { Button, Modal, ModalBody } from 'reactstrap';

/**
 * Props for {@link DownloadOptionsDialog}.
 *
 * @property isOpen - Whether the modal is visible. Parent keeps the state.
 * @property onClose - Fired when the user dismisses via the close X (or Esc).
 * @property onDownloadWithAnnotations - User picked the flattened variant.
 *   Parent should call `downloadFlattenedPdf(instance)`.
 * @property onDownloadOriginal - User picked the original uploaded file.
 *   Parent should stream the raw S3 URL.
 */
type Props = {
  isOpen: boolean;
  onClose: () => void;
  onDownloadWithAnnotations: () => void;
  onDownloadOriginal: () => void;
};

/**
 * Shown when the user clicks Download on a PDF that has at least one
 * annotation. Lets them choose between the flattened version (annotations
 * baked in) and the original upload. PDFs with no annotations download
 * directly without this prompt.
 *
 * Follows Liscio's ConfirmationModal DOM + class convention so styling stays
 * consistent with the rest of the firm-side modals.
 */
const TITLE_ID = 'download-options-dialog-title';

export default function DownloadOptionsDialog({
  isOpen,
  onClose,
  onDownloadWithAnnotations,
  onDownloadOriginal,
}: Props) {
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      labelledBy={TITLE_ID}
      className="customModal customModal--center customModal--alert customModal--w350 customModal--nopadding"
    >
      <div className="ModalHeader">
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={onClose}
        >
          <i className="icon-close2" aria-hidden="true" />
        </button>
      </div>
      <ModalBody className="modalContent">
        <div className="modalContent--inner">
          <div className="modalContent--header text-large">
            <h5 className="modalContent--header__heading" id={TITLE_ID}>
              Download
            </h5>
          </div>
          <p>This PDF has annotations. Include them in the download?</p>
          <div className="row">
            <div className="col-12 justify-content-end formbtn">
              <div className="btn-wrap btn--leftspace">
                <Button
                  className="btn btn-outline-light"
                  onClick={onDownloadOriginal}
                >
                  Original
                </Button>
                <Button
                  className="btn btn-primary"
                  onClick={onDownloadWithAnnotations}
                  autoFocus
                >
                  With annotations
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
